import React from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { TextButtonLink } from 'js/design_system/link/TextButtonLink';
import PremiumLock from 'js/premium/PremiumLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { NavButtonLink } from './buttonStyles';
import { ChurnedUserReactivationDiscount } from './types';

const TRIGGER = 'reactivate_button';

const Text = styled.div`
  white-space: inherit;
  text-align: center;
`;

const NoWrapSpan = styled.span`
  white-space: nowrap;
`;

// Autotest v_react_discount: the real discount as a distinct gold pill on the
// reactivate CTA (vs control's inline "X OFF" text).
// Keeps "Reactivate Premium" + the badge on a single, vertically-centred line so
// the pill sits beside the label (not dropped onto its own row). The button is
// fit-content (max 268px), so it grows to fit.
const BadgeRow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 15px;
`;

const DiscountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--premium-gold, #877440);
  color: var(--background-white, #ffffff);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.5;
`;

const MobileText = styled.div`
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MobileNavButtonContainer = styled.div`
  flex-shrink: 1;
  --buttons-small-buttons-height: 40px;
  --buttons-small-buttons-l-r-padding: var(--spacing-xs-8, 8px);
`;

const ButtonWrapper = styled.div`
  width: fit-content;
  min-width: 220px;
  max-width: 268px;
  flex: 0 0 auto;
`;

type ChurnedUserReactivationButtonProps = {
  discount: ChurnedUserReactivationDiscount;
  isMobileNav: boolean;
  /**
   * Reactivation autotest copy override (AutotestFocusedNavReactivation
   * `reactivateLabel` surface). null/undefined (control) keeps the offer-derived
   * text below unchanged.
   */
  reactivateLabel?: string | null;
  /**
   * Reactivation autotest discount-badge override (`reactivateDiscountBadge`
   * surface): render the discount as a distinct gold pill instead of inline text.
   * Only takes effect for discount offers.
   */
  reactivateDiscountBadge?: boolean;
};

export default function ChurnedUserReactivationButton({
  discount: { offer, value },
  isMobileNav,
  reactivateLabel,
  reactivateDiscountBadge,
}: ChurnedUserReactivationButtonProps) {
  const checkoutUrl = routes.checkout_path({
    feature: 'churned_user_reactivation',
    trigger: TRIGGER,
    from_navbar: true,
  });

  const offerButtonContent =
    offer === 'discount' ? (
      <Text>
        Reactivate Premium, <NoWrapSpan>{value} OFF</NoWrapSpan>
      </Text>
    ) : (
      <Text>
        Reactivate Premium <NoWrapSpan>for {value}</NoWrapSpan>
      </Text>
    );

  const offerMobileButtonContent =
    offer === 'discount' ? (
      <MobileText>Reactivate Premium, {value} OFF</MobileText>
    ) : (
      <MobileText>Reactivate Premium for {value}</MobileText>
    );

  // Autotest overrides (control leaves both unset → offer-derived text unchanged;
  // url/trigger always stay tied to the offer). reactivateLabel (copy) and
  // reactivateDiscountBadge are mutually exclusive across arms; copy wins if both.
  const showBadge =
    !reactivateLabel && reactivateDiscountBadge && offer === 'discount';
  const buttonContent = reactivateLabel ? (
    <Text>{reactivateLabel}</Text>
  ) : showBadge ? (
    <Text>
      <BadgeRow>
        Reactivate Premium <DiscountBadge>{value} OFF</DiscountBadge>
      </BadgeRow>
    </Text>
  ) : (
    offerButtonContent
  );
  const mobileButtonContent = reactivateLabel ? (
    <MobileText>{reactivateLabel}</MobileText>
  ) : showBadge ? (
    <MobileText>
      <BadgeRow>
        Reactivate Premium <DiscountBadge>{value} OFF</DiscountBadge>
      </BadgeRow>
    </MobileText>
  ) : (
    offerMobileButtonContent
  );

  if (isMobileNav) {
    return (
      <ImpressionWatcher source={TRIGGER} extra={{ offer }}>
        <MobileNavButtonContainer>
          <TextButtonLink
            href={checkoutUrl}
            icon={<PremiumLock circleColor="gold" lockColor="white" />}
            trackClick={{ source: 'churned_user_reactivation_button' }}
            size="sm"
          >
            {mobileButtonContent}
          </TextButtonLink>
        </MobileNavButtonContainer>
      </ImpressionWatcher>
    );
  }

  return (
    <ButtonWrapper>
      <ImpressionWatcher source={TRIGGER} extra={{ offer }}>
        <NavButtonLink
          href={checkoutUrl}
          icon={<PremiumLock circleColor="white" lockColor="blue" />}
          trackClick={{ source: 'churned_user_reactivation_button' }}
        >
          {buttonContent}
        </NavButtonLink>
      </ImpressionWatcher>
    </ButtonWrapper>
  );
}
